/**
 * EditUserModal — thin wrapper around @insio/ui-toolkit module.
 * Integrates GraphQL mutation, resolves role translations and lists the user's org positions.
 */
import { useId } from 'react';
import { useMutation } from '@apollo/client/react';
import { uniq } from 'es-toolkit';
import { Badge, EditUserModal as EditUserModalBase } from '@insio/ui-toolkit';
import { type UserRole, UpdateAccountUserDocument } from '@/graphql';
import { ASSIGNABLE_ROLES, getAssignableRoles } from '@/features/admin/constants/roles';
import { useRoleOptions } from '@/features/admin/hooks/use-role-options';
import { getUserManagementErrorKey } from '@/features/admin/utils/map-user-management-error';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import type { UserOrgUnit } from '../types';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  identitySharedWithOtherAccounts: boolean;
  orgUnits: readonly UserOrgUnit[];
}

interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditUserModal({ user, onClose, onSuccess }: EditUserModalProps) {
  const t = useFeatureTranslation('systemSettings.users');
  const { options: roleOptions } = useRoleOptions();
  const [updateUser, { loading, error }] = useMutation(UpdateAccountUserDocument);

  const errorKey = getUserManagementErrorKey(error);
  const errorMessage = errorKey ? t(errorKey) : error?.message;
  const nameLocked = user.identitySharedWithOtherAccounts;

  const handleSubmit = async (data: { firstName: string; lastName: string; roles: string[] }) => {
    // A role the modal did not offer (see useRoleOptions) is never a deliberate removal — keep
    // it, or a plain USERS_MANAGE edit would silently strip master account or Tappeer access.
    const offered = new Set<string>(roleOptions.map(option => option.value));
    const hidden = user.roles.filter(
      role => (ASSIGNABLE_ROLES as readonly string[]).includes(role) && !offered.has(role)
    );
    const roles = uniq([...data.roles, ...hidden]);

    const result = await updateUser({
      variables: {
        userId: user.id,
        input: {
          // Only the user changes the name of an identity shared with another account.
          ...(nameLocked
            ? {}
            : { firstName: data.firstName.trim(), lastName: data.lastName.trim() }),
          roles: roles as UserRole[]
        }
      }
    });

    if (result.data?.updateAccountUser) {
      onSuccess();
    }
  };

  return (
    <EditUserModalBase
      open={true}
      user={user}
      roleOptions={roleOptions}
      getAssignableRoles={getAssignableRoles}
      nameReadOnly={nameLocked}
      notice={nameLocked ? t('modals.edit.sharedIdentityNotice') : undefined}
      onSubmit={handleSubmit}
      onClose={onClose}
      loading={loading}
      error={errorMessage}
    >
      <OrgPositions userId={user.id} orgUnits={user.orgUnits} />
    </EditUserModalBase>
  );
}

/** Read-only list of the org units the user belongs to, marking the ones they head. */
function OrgPositions({ userId, orgUnits }: { userId: string; orgUnits: readonly UserOrgUnit[] }) {
  const t = useFeatureTranslation('systemSettings.users.orgPositions');
  const tOrgStructure = useFeatureTranslation('codeLists.orgStructure');
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className='flex flex-col gap-xs'
      data-testid='edit-user-org-positions'
    >
      <h3 id={headingId} className='flex h-6 items-center text-sm text-foreground-muted'>
        {t('title')}
      </h3>
      {orgUnits.length > 0 ? (
        <ul className='flex flex-col gap-xs'>
          {[...orgUnits]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(unit => (
              <li key={unit.id} className='flex min-w-0 items-center gap-sm text-sm'>
                <span className='truncate text-foreground-base'>{unit.name}</span>
                <span className='shrink-0 font-mono text-xs text-foreground-muted'>
                  {unit.code}
                </span>
                {unit.manager?.id === userId && (
                  <Badge color='neutral' size='sm' data-testid={`edit-user-org-head-${unit.id}`}>
                    {tOrgStructure('headTag')}
                  </Badge>
                )}
              </li>
            ))}
        </ul>
      ) : (
        <p className='text-sm text-foreground-muted'>{t('empty')}</p>
      )}
    </section>
  );
}
